import { 
  Building2, 
  Users, 
  FileText, 
  Wrench, 
  LayoutDashboard, 
  LogOut,
  Wallet,
  Plane,
  Settings,
  ShieldCheck,
  ChevronRight,
  ChevronLeft,
  X
} from 'lucide-react';

interface SidebarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  onLogout?: () => void;
  permissions?: any; // To hide/show tabs
  isCollapsed: boolean;
  setIsCollapsed: (v: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ activeTab, setActiveTab, onLogout, permissions, isCollapsed, setIsCollapsed }) => {
  const menuItems = [
    { id: 'dashboard', label: 'لوحة التحكم', icon: LayoutDashboard, permission: 'dashboard' },
    { id: 'properties', label: 'العقارات والوحدات', icon: Building2, permission: 'properties' },
    { id: 'tenants', label: 'المستأجرين', icon: Users, permission: 'tenants' },
    { id: 'invoices', label: 'الفواتير والتحصيل', icon: FileText, permission: 'invoices' },
    { id: 'expenses', label: 'الإيرادات والمصروفات', icon: Wallet, permission: 'expenses' },
    { id: 'maintenance', label: 'الصيانة', icon: Wrench, permission: 'maintenance' },
    { id: 'employees', label: 'إدارة الموظفين', icon: Users, permission: 'employees' },
    { id: 'reports', label: 'التقارير المالية', icon: FileText, permission: 'reports' },
    { id: 'userManagement', label: 'إدارة المستخدمين', icon: ShieldCheck, permission: 'userManagement' },
    { id: 'settings', label: 'الإعدادات', icon: Settings, permission: 'settings' },
  ];

  const visibleItems = menuItems.filter(item => {
      if (!permissions) return true; // Default to all if no permission object (Admin)
      return permissions[item.permission] !== false;
  });

  const handleItemClick = (id: string) => {
    setActiveTab(id);
    if (window.innerWidth < 768) {
      setIsCollapsed(true);
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {!isCollapsed && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden backdrop-blur-sm"
          onClick={() => setIsCollapsed(true)}
        ></div>
      )}

      <div className={`
        fixed inset-y-0 right-0 z-50 transform transition-transform duration-300 ease-in-out
        ${isCollapsed ? 'translate-x-full' : 'translate-x-0'}
        md:relative md:translate-x-0
        w-72 max-w-[85vw] ${isCollapsed ? 'md:w-20' : 'md:w-64'} 
        bg-slate-900 text-white h-screen flex flex-col shadow-2xl font-sans sidebar-transition
      `}>
      <div className={`p-6 border-b border-slate-800 flex items-center ${isCollapsed ? 'justify-center' : 'justify-between gap-3'} overflow-hidden`}>
        <div className="flex items-center gap-3">
          <div className="bg-fly-500 p-2 rounded-lg shrink-0">
             <Plane className="w-6 h-6 text-white" />
          </div>
          {!isCollapsed && (
            <div className="whitespace-nowrap transition-all duration-300">
               <h1 className="text-2xl font-bold tracking-tight">Fly <span className="text-fly-400">Rent</span></h1>
               <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">Real Estate System</p>
            </div>
          )}
        </div>
        {!isCollapsed && (
          <button 
            type="button"
            onClick={() => setIsCollapsed(true)}
            className="md:hidden text-slate-400 hover:text-white p-1 rounded-lg"
          >
            <X size={20} />
          </button>
        )}
      </div>
      
      <nav className="flex-1 overflow-y-auto py-6 custom-scrollbar overflow-x-hidden">
        <ul className="space-y-2 px-3">
          {visibleItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <li key={item.id}>
                <button
                  onClick={() => handleItemClick(item.id)}
                  title={isCollapsed ? item.label : ''}
                  className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3 space-x-reverse px-4'} py-3.5 rounded-xl transition-all duration-200 group ${
                    isActive 
                      ? 'bg-fly-600 text-white font-semibold shadow-lg shadow-fly-900/20' 
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <Icon className={`w-5 h-5 shrink-0 transition-colors ${isActive ? 'text-white' : 'text-slate-500 group-hover:text-white'}`} />
                  {!isCollapsed && <span className="whitespace-nowrap">{item.label}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="p-4 border-t border-slate-800 space-y-2">
        <button 
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="flex items-center justify-center text-slate-500 hover:text-white w-full p-2 hover:bg-slate-800 rounded-xl transition duration-200"
        >
          {isCollapsed ? <ChevronLeft size={20} /> : <div className="flex items-center gap-2 text-xs"><ChevronRight size={18} /> <span>تصغير القائمة</span></div>}
        </button>

        <button onClick={onLogout} className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3 space-x-reverse px-4'} text-slate-400 hover:text-red-400 w-full py-3 hover:bg-slate-800 rounded-xl transition duration-200 group`}>
          <LogOut className="w-5 h-5 shrink-0 group-hover:text-red-400 transition-colors" />
          {!isCollapsed && <span className="font-medium whitespace-nowrap">تسجيل الخروج</span>}
        </button>
      </div>
    </div>
    </>
  );
};

export default Sidebar;